'use client';
import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import AppNav from '@/components/AppNav';
import Link from 'next/link';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FilingPackage {
  id: string;
  name: string;
  description: string;
  category: string;
  govtFee: number;
  platformFee: number;
  professionalFee: number;
  timeline: string;
  includes: string[];
}

interface FeeBreakdown {
  govtFee: number;
  platformFee: number;
  professionalFee: number;
  subtotal: number;
  gst: number;
  total: number;
}

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
}

type CheckoutStep = 'summary' | 'payment' | 'processing' | 'success' | 'failure';

// ─── Package Catalogue ───────────────────────────────────────────────────────

const PACKAGES: Record<string, FilingPackage> = {
  company_registration: {
    id: 'company_registration',
    name: 'Private Limited Company Registration',
    description: 'Complete incorporation of a Private Limited Company with MCA filing, DIN, DSC, and Certificate of Incorporation.',
    category: 'Company Formation',
    govtFee: 2000,
    platformFee: 999,
    professionalFee: 4999,
    timeline: '10–15 working days',
    includes: ['DIN for 2 Directors', 'DSC (Class 3)', 'MOA & AOA Drafting', 'MCA Filing', 'Certificate of Incorporation', 'PAN & TAN Application'],
  },
  trademark_filing: {
    id: 'trademark_filing',
    name: 'Trademark Registration',
    description: 'End-to-end trademark application filing with IP India, including search report and objection support.',
    category: 'Intellectual Property',
    govtFee: 4500,
    platformFee: 999,
    professionalFee: 2999,
    timeline: '18–24 months (government process)',
    includes: ['Trademark Search Report', 'Class Selection Guidance', 'Application Filing', 'Acknowledgement Certificate', 'Objection Reply (1 round)', 'Status Tracking'],
  },
  gst_registration: {
    id: 'gst_registration',
    name: 'GST Registration',
    description: 'GST registration on the GSTN portal with ARN generation and GSTIN issuance.',
    category: 'Tax Compliance',
    govtFee: 0,
    platformFee: 499,
    professionalFee: 1499,
    timeline: '3–7 working days',
    includes: ['Document Preparation', 'GSTN Portal Filing', 'ARN Generation', 'GSTIN Certificate', 'First Return Guidance'],
  },
  msme_registration: {
    id: 'msme_registration',
    name: 'MSME / Udyam Registration',
    description: 'Udyam registration on the MSME portal for micro, small, and medium enterprises.',
    category: 'Government Registration',
    govtFee: 0,
    platformFee: 299,
    professionalFee: 999,
    timeline: '1–2 working days',
    includes: ['Udyam Portal Filing', 'Udyam Certificate', 'MSME Benefits Guide', 'Bank Credit Linkage Info'],
  },
  dpiit_recognition: {
    id: 'dpiit_recognition',
    name: 'DPIIT Startup Recognition',
    description: 'DPIIT recognition for startups on the Startup India portal with certificate and benefits activation.',
    category: 'Startup Benefits',
    govtFee: 0,
    platformFee: 499,
    professionalFee: 1999,
    timeline: '5–10 working days',
    includes: ['Eligibility Assessment', 'DIPP Portal Filing', 'Recognition Certificate', 'Tax Exemption Guidance', 'Fund Access Support'],
  },
  annual_compliance: {
    id: 'annual_compliance',
    name: 'Annual Compliance Package',
    description: 'Complete annual compliance for Private Limited Companies including ROC filings, board meetings, and statutory registers.',
    category: 'Compliance',
    govtFee: 1200,
    platformFee: 999,
    professionalFee: 7999,
    timeline: 'Ongoing (annual)',
    includes: ['4 Board Meeting Minutes', 'Annual General Meeting', 'MGT-7 (Annual Return)', 'AOC-4 (Financial Statements)', 'Statutory Registers', 'Director KYC'],
  },
};

const DEFAULT_PACKAGE = PACKAGES['company_registration'];

// ─── Fee Calculator ───────────────────────────────────────────────────────────

function calcFees(pkg: FilingPackage): FeeBreakdown {
  const subtotal = pkg.govtFee + pkg.platformFee + pkg.professionalFee;
  // GST at 18% on platform + professional fees only (govt fee is exempt)
  const gstBase = pkg.platformFee + pkg.professionalFee;
  const gst = Math.round(gstBase * 0.18);
  const total = subtotal + gst;
  return { govtFee: pkg.govtFee, platformFee: pkg.platformFee, professionalFee: pkg.professionalFee, subtotal, gst, total };
}

// ─── Receipt Component ────────────────────────────────────────────────────────

function Receipt({ pkg, fees, orderId, customerInfo, paidAt }: {
  pkg: FilingPackage;
  fees: FeeBreakdown;
  orderId: string;
  customerInfo: CustomerInfo;
  paidAt: string;
}) {
  const handlePrint = () => window.print();

  return (
    <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card">
      {/* Receipt Header */}
      <div className="bg-gradient-to-r from-ai to-ai-dark px-6 py-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-bold">B</span>
              </div>
              <span className="font-semibold text-[15px]">BusinessOS by Nevika.ai</span>
            </div>
            <p className="text-white/70 text-[12px]">Official Payment Receipt</p>
          </div>
          <div className="text-right">
            <p className="text-white/70 text-[11px] uppercase tracking-wider">Receipt No.</p>
            <p className="font-mono font-bold text-[13px]">{orderId.slice(0, 20)}</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Status Badge */}
        <div className="flex items-center gap-2 p-3 bg-success/8 border border-success/20 rounded-xl">
          <div className="w-8 h-8 bg-success/15 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-[13px] font-semibold text-success">Payment Successful</p>
            <p className="text-[11px] text-ink-muted">{paidAt}</p>
          </div>
        </div>

        {/* Customer Details */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] font-semibold text-ink-subtle uppercase tracking-wider mb-1">Billed To</p>
            <p className="text-[13px] font-semibold text-ink">{customerInfo.name}</p>
            <p className="text-[12px] text-ink-muted">{customerInfo.email}</p>
            <p className="text-[12px] text-ink-muted">{customerInfo.phone}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-ink-subtle uppercase tracking-wider mb-1">Service Provider</p>
            <p className="text-[13px] font-semibold text-ink">Nevika.ai Technologies</p>
            <p className="text-[12px] text-ink-muted">GSTIN: 07AABCN1234F1Z5</p>
            <p className="text-[12px] text-ink-muted">CIN: U74999DL2024PTC123456</p>
          </div>
        </div>

        {/* Package Details */}
        <div className="bg-surface-2 rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <span className="text-[10px] font-bold text-ai uppercase tracking-wider bg-ai-light px-2 py-0.5 rounded-full">{pkg.category}</span>
              <p className="text-[14px] font-semibold text-ink mt-2">{pkg.name}</p>
              <p className="text-[12px] text-ink-muted mt-0.5">{pkg.description}</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {pkg.includes.map((item, i) => (
              <span key={i} className="text-[10px] bg-white border border-border text-ink-muted px-2 py-0.5 rounded-full">{item}</span>
            ))}
          </div>
        </div>

        {/* Fee Breakdown */}
        <div className="space-y-2">
          <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wider">Fee Breakdown</p>
          <div className="space-y-1.5">
            {fees.govtFee > 0 && (
              <div className="flex justify-between items-center py-1.5 border-b border-border-subtle">
                <div>
                  <p className="text-[13px] text-ink">Government Fee</p>
                  <p className="text-[11px] text-ink-subtle">Statutory charges payable to government</p>
                </div>
                <p className="text-[13px] font-medium text-ink">₹{fees.govtFee.toLocaleString('en-IN')}</p>
              </div>
            )}
            <div className="flex justify-between items-center py-1.5 border-b border-border-subtle">
              <div>
                <p className="text-[13px] text-ink">Platform Fee</p>
                <p className="text-[11px] text-ink-subtle">Technology & processing charges</p>
              </div>
              <p className="text-[13px] font-medium text-ink">₹{fees.platformFee.toLocaleString('en-IN')}</p>
            </div>
            <div className="flex justify-between items-center py-1.5 border-b border-border-subtle">
              <div>
                <p className="text-[13px] text-ink">Professional Fee</p>
                <p className="text-[11px] text-ink-subtle">CA/CS/Advocate charges</p>
              </div>
              <p className="text-[13px] font-medium text-ink">₹{fees.professionalFee.toLocaleString('en-IN')}</p>
            </div>
            <div className="flex justify-between items-center py-1.5 border-b border-border-subtle">
              <p className="text-[13px] text-ink-muted">Subtotal</p>
              <p className="text-[13px] text-ink-muted">₹{fees.subtotal.toLocaleString('en-IN')}</p>
            </div>
            <div className="flex justify-between items-center py-1.5 border-b border-border-subtle">
              <div>
                <p className="text-[13px] text-ink">GST (18%)</p>
                <p className="text-[11px] text-ink-subtle">On platform + professional fee</p>
              </div>
              <p className="text-[13px] font-medium text-ink">₹{fees.gst.toLocaleString('en-IN')}</p>
            </div>
            <div className="flex justify-between items-center pt-2">
              <p className="text-[15px] font-bold text-ink">Total Paid</p>
              <p className="text-[18px] font-bold text-ai">₹{fees.total.toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex items-center gap-2 p-3 bg-warning/8 border border-warning/20 rounded-xl">
          <svg className="w-4 h-4 text-warning flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-[12px] font-semibold text-warning">Expected Timeline</p>
            <p className="text-[11px] text-ink-muted">{pkg.timeline}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-xl text-[13px] font-medium text-ink-muted hover:text-ink hover:bg-surface-2 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Receipt
          </button>
          <Link
            href="/dashboard"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-ai text-white rounded-xl text-[13px] font-semibold hover:bg-ai-dark transition-all shadow-sm"
          >
            Go to Dashboard
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Main Checkout Component ──────────────────────────────────────────────────

function CheckoutContent() {
  const searchParams = useSearchParams();
  const router = useRouter();

  const packageId = searchParams.get('package') || 'company_registration';
  const pkg = PACKAGES[packageId] || DEFAULT_PACKAGE;
  const fees = calcFees(pkg);

  const [step, setStep] = useState<CheckoutStep>('summary');
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({ name: '', email: '', phone: '' });
  const [errors, setErrors] = useState<Partial<CustomerInfo>>({});
  const [orderId, setOrderId] = useState('');
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [apiError, setApiError] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const paymentContainerRef = useRef<HTMLDivElement>(null);
  const cashfreeRef = useRef<unknown>(null);

  // Load Cashfree JS SDK
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existing = document.getElementById('cashfree-sdk');
    if (existing) return;
    const script = document.createElement('script');
    script.id = 'cashfree-sdk';
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Mount Cashfree drop-in when paymentSessionId is ready
  useEffect(() => {
    if (!paymentSessionId || step !== 'payment') return;

    const mountWidget = () => {
      const win = window as unknown as Record<string, unknown>;
      if (typeof win['Cashfree'] !== 'function') {
        setTimeout(mountWidget, 300);
        return;
      }
      try {
        const cashfree = (win['Cashfree'] as (opts: unknown) => unknown)({ mode: 'production' });
        cashfreeRef.current = cashfree;

        const checkoutOptions = {
          paymentSessionId,
          returnUrl: `${window.location.origin}/checkout/result?order_id=${orderId}`,
          redirectTarget: '_self',
        };

        (cashfree as { checkout: (opts: unknown) => Promise<{ error?: { message: string }; redirect?: boolean }> })
          .checkout(checkoutOptions)
          .then((result) => {
            if (result?.error) {
              setApiError(result.error.message || 'Payment failed. Please try again.');
              setStep('failure');
            } else if (result?.redirect) {
              setStep('processing');
            }
          })
          .catch(() => {
            setApiError('Payment could not be initiated. Please try again.');
            setStep('failure');
          });
      } catch {
        setApiError('Failed to load payment widget. Please refresh and try again.');
        setStep('failure');
      }
    };

    const timer = setTimeout(mountWidget, 500);
    return () => clearTimeout(timer);
  }, [paymentSessionId, step, orderId]);

  const validateForm = (): boolean => {
    const newErrors: Partial<CustomerInfo> = {};
    if (!customerInfo.name.trim()) newErrors.name = 'Full name is required';
    if (!customerInfo.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) newErrors.email = 'Valid email is required';
    if (!customerInfo.phone.trim() || !/^[6-9]\d{9}$/.test(customerInfo.phone.replace(/\D/g, ''))) newErrors.phone = 'Valid 10-digit Indian mobile number required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceedToPayment = async () => {
    if (!validateForm()) return;
    setIsCreatingOrder(true);
    setApiError('');

    try {
      const res = await fetch('/api/payments/cashfree/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderAmount: fees.total,
          customerName: customerInfo.name,
          customerEmail: customerInfo.email,
          customerPhone: customerInfo.phone,
          packageId: pkg.id,
          packageName: pkg.name,
        }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        setApiError(data.error || 'Failed to create order. Please try again.');
        setIsCreatingOrder(false);
        return;
      }

      setOrderId(data.orderId);
      setPaymentSessionId(data.paymentSessionId);
      setStep('payment');
    } catch {
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleRetry = () => {
    setStep('summary');
    setApiError('');
    setPaymentSessionId('');
    setOrderId('');
  };

  // ── Render: Success ──────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="max-w-2xl mx-auto">
        <Receipt pkg={pkg} fees={fees} orderId={orderId} customerInfo={customerInfo} paidAt={paidAt} />
      </div>
    );
  }

  // ── Render: Failure ──────────────────────────────────────────────────────────
  if (step === 'failure') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white border border-border rounded-2xl p-8 shadow-card text-center">
          <div className="w-16 h-16 bg-danger/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-[20px] font-bold text-ink mb-2">Payment Failed</h2>
          <p className="text-[14px] text-ink-muted mb-2">{apiError || 'Your payment could not be processed.'}</p>
          {orderId && <p className="text-[12px] text-ink-subtle mb-6 font-mono">Order ID: {orderId}</p>}
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleRetry}
              className="px-6 py-2.5 bg-ai text-white rounded-xl text-[13px] font-semibold hover:bg-ai-dark transition-all shadow-sm"
            >
              Try Again
            </button>
            <Link href="/dashboard" className="px-6 py-2.5 border border-border rounded-xl text-[13px] font-medium text-ink-muted hover:text-ink hover:bg-surface-2 transition-all">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Processing ───────────────────────────────────────────────────────
  if (step === 'processing') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white border border-border rounded-2xl p-10 shadow-card text-center">
          <div className="w-16 h-16 bg-ai-light rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-ai animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <h2 className="text-[20px] font-bold text-ink mb-2">Processing Payment</h2>
          <p className="text-[14px] text-ink-muted">Please wait while we confirm your payment. Do not close this window.</p>
        </div>
      </div>
    );
  }

  // ── Render: Payment Widget ───────────────────────────────────────────────────
  if (step === 'payment') {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Order Summary Strip */}
        <div className="bg-white border border-border rounded-2xl p-4 shadow-card flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wider">Paying for</p>
            <p className="text-[14px] font-semibold text-ink">{pkg.name}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wider">Total</p>
            <p className="text-[20px] font-bold text-ai">₹{fees.total.toLocaleString('en-IN')}</p>
          </div>
        </div>

        {/* Cashfree Widget Container */}
        <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse-soft" />
            <p className="text-[13px] font-semibold text-ink">Secure Payment via Cashfree</p>
            <div className="ml-auto flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-[11px] text-ink-subtle">256-bit SSL</span>
            </div>
          </div>
          <div className="p-5">
            <div ref={paymentContainerRef} id="cashfree-payment-container" className="min-h-[200px] flex items-center justify-center">
              <div className="text-center">
                <svg className="w-8 h-8 text-ai animate-spin mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className="text-[13px] text-ink-muted">Loading payment options…</p>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handleRetry}
          className="w-full py-2.5 text-[13px] text-ink-muted hover:text-ink transition-colors flex items-center justify-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Order Summary
        </button>
      </div>
    );
  }

  // ── Render: Summary (default) ────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 max-w-5xl mx-auto">
      {/* Left: Customer Form */}
        <div className="lg:col-span-3 space-y-4">
        {/* Package Card */}
        <div className="bg-white border border-border rounded-2xl p-5 shadow-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-ai-light rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-ai text-lg">📋</span>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-bold text-ai uppercase tracking-wider bg-ai-light px-2 py-0.5 rounded-full">{pkg.category}</span>
              <h2 className="text-[16px] font-bold text-ink mt-1.5">{pkg.name}</h2>
              <p className="text-[13px] text-ink-muted mt-0.5">{pkg.description}</p>
              <div className="flex items-center gap-1.5 mt-2">
                <svg className="w-3.5 h-3.5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-[12px] text-ink-muted">{pkg.timeline}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {pkg.includes.map((item, i) => (
              <span key={i} className="text-[11px] bg-surface-2 border border-border text-ink-muted px-2.5 py-1 rounded-full flex items-center gap-1">
                <svg className="w-3 h-3 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Customer Info Form */}
        <div className="bg-white border border-border rounded-2xl p-5 shadow-card">
          <h3 className="text-[14px] font-semibold text-ink mb-4 flex items-center gap-2">
            <svg className="w-4 h-4 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Your Details
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold text-ink-muted mb-1.5">Full Name *</label>
              <input
                type="text"
                value={customerInfo.name}
                onChange={e => setCustomerInfo(p => ({ ...p, name: e.target.value }))}
                placeholder="Arjun Sharma"
                className={`w-full px-3.5 py-2.5 border rounded-xl text-[13px] text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai transition-all ${errors.name ? 'border-danger' : 'border-border'}`}
              />
              {errors.name && <p className="text-[11px] text-danger mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-ink-muted mb-1.5">Email Address *</label>
              <input
                type="email"
                value={customerInfo.email}
                onChange={e => setCustomerInfo(p => ({ ...p, email: e.target.value }))}
                placeholder="arjun@company.com"
                className={`w-full px-3.5 py-2.5 border rounded-xl text-[13px] text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai transition-all ${errors.email ? 'border-danger' : 'border-border'}`}
              />
              {errors.email && <p className="text-[11px] text-danger mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-ink-muted mb-1.5">Mobile Number *</label>
              <div className="flex flex-col xs:flex-row gap-2">
                <div className="flex items-center px-3 border border-border rounded-xl bg-surface-2 text-[13px] text-ink-muted font-medium h-10 w-fit">+91</div>
                <input
                  type="tel"
                  value={customerInfo.phone}
                  onChange={e => setCustomerInfo(p => ({ ...p, phone: e.target.value }))}
                  placeholder="9876543210"
                  maxLength={10}
                  className={`flex-1 px-3.5 py-2.5 border rounded-xl text-[13px] text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai transition-all ${errors.phone ? 'border-danger' : 'border-border'}`}
                />
              </div>
              {errors.phone && <p className="text-[11px] text-danger mt-1">{errors.phone}</p>}
            </div>
          </div>
        </div>

        {/* Package Selector */}
        <div className="bg-white border border-border rounded-2xl p-5 shadow-card">
          <h3 className="text-[14px] font-semibold text-ink mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Other Packages
          </h3>
          <div className="space-y-2">
            {Object.values(PACKAGES).map(p => (
              <Link
                key={p.id}
                href={`/checkout?package=${p.id}`}
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-[13px] transition-all ${p.id === pkg.id ? 'border-ai bg-ai-light text-ai font-semibold' : 'border-border text-ink-muted hover:border-ai/40 hover:bg-surface-2'}`}
              >
                <span>{p.name}</span>
                <span className="font-semibold">₹{calcFees(p).total.toLocaleString('en-IN')}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Order Summary */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card lg:sticky lg:top-6">
          <div className="px-5 py-4 border-b border-border bg-surface-2">
            <h3 className="text-[14px] font-semibold text-ink">Order Summary</h3>
          </div>
          <div className="p-5 space-y-3">
            {/* Fee Lines */}
            {fees.govtFee > 0 && (
              <div className="flex justify-between items-start gap-2">
                <div>
                  <p className="text-[13px] text-ink">Government Fee</p>
                  <p className="text-[11px] text-ink-subtle">Statutory charges</p>
                </div>
                <p className="text-[13px] font-medium text-ink flex-shrink-0">₹{fees.govtFee.toLocaleString('en-IN')}</p>
              </div>
            )}
            <div className="flex justify-between items-start gap-2">
              <div>
                <p className="text-[13px] text-ink">Platform Fee</p>
                <p className="text-[11px] text-ink-subtle">Technology & processing</p>
              </div>
              <p className="text-[13px] font-medium text-ink flex-shrink-0">₹{fees.platformFee.toLocaleString('en-IN')}</p>
            </div>
            <div className="flex justify-between items-start gap-2">
              <div>
                <p className="text-[13px] text-ink">Professional Fee</p>
                <p className="text-[11px] text-ink-subtle">CA/CS/Advocate</p>
              </div>
              <p className="text-[13px] font-medium text-ink flex-shrink-0">₹{fees.professionalFee.toLocaleString('en-IN')}</p>
            </div>
            <div className="border-t border-border-subtle pt-3 flex justify-between items-center">
              <p className="text-[12px] text-ink-muted">Subtotal</p>
              <p className="text-[12px] text-ink-muted">₹{fees.subtotal.toLocaleString('en-IN')}</p>
            </div>
            <div className="flex justify-between items-start gap-2">
              <div>
                <p className="text-[13px] text-ink">GST (18%)</p>
                <p className="text-[11px] text-ink-subtle">On platform + professional fee</p>
              </div>
              <p className="text-[13px] font-medium text-ink flex-shrink-0">₹{fees.gst.toLocaleString('en-IN')}</p>
            </div>
            <div className="border-t border-border pt-3 flex justify-between items-center">
              <p className="text-[15px] font-bold text-ink">Total</p>
              <p className="text-[22px] font-bold text-ai">₹{fees.total.toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="px-5 pb-4 space-y-2">
            {[
              { icon: '🔒', text: '256-bit SSL encrypted payment' },
              { icon: '🏦', text: 'Powered by Cashfree Payments' },
              { icon: '📄', text: 'GST invoice provided' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-sm">{b.icon}</span>
                <p className="text-[11px] text-ink-subtle">{b.text}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          {apiError && (
            <div className="mx-5 mb-3 p-3 bg-danger/8 border border-danger/20 rounded-xl">
              <p className="text-[12px] text-danger">{apiError}</p>
            </div>
          )}
          <div className="px-5 pb-5">
            <button
              onClick={handleProceedToPayment}
              disabled={isCreatingOrder}
              className="w-full py-3 bg-ai text-white rounded-xl text-[14px] font-bold hover:bg-ai-dark transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCreatingOrder ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating Order…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Proceed to Pay ₹{fees.total.toLocaleString('en-IN')}
                </>
              )}
            </button>
            <p className="text-[10px] text-ink-subtle text-center mt-2">
              By proceeding, you agree to our{' '}
              <a href="#" className="text-ai hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-ai hover:underline">Refund Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page Wrapper ─────────────────────────────────────────────────────────────

export default function CheckoutPage() {
  return (
    <div className="min-h-screen bg-surface-2">
      <AppNav activePath="/checkout" />
      <main className="sm:pl-60 pt-14 sm:pt-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-[12px] text-ink-muted mb-3">
              <Link href="/dashboard" className="hover:text-ink transition-colors">Dashboard</Link>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-ink font-medium">Checkout</span>
            </div>
            <h1 className="text-[20px] sm:text-[24px] font-bold text-ink">Secure Checkout</h1>
            <p className="text-[13px] sm:text-[14px] text-ink-muted mt-0.5">Complete your filing package purchase</p>
          </div>

          <Suspense fallback={
            <div className="flex items-center justify-center py-20">
              <svg className="w-8 h-8 text-ai animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          }>
            <CheckoutContent />
          </Suspense>
        </div>
      </main>
    </div>
  );
}
